import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider, useI18n, storedLang } from "@/i18n";
import { DEFAULT_LANG, SEO_LANGS, browserLang, pathForLang, privacyPathForLang } from "@/lib/seo";
import { FullScreen } from "@/components/FullScreen";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminDashboard from "@/pages/AdminDashboard";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ScreeningApp from "@/components/ScreeningApp";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (loading) return <FullScreen text={t("loading")} />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

// The root is the x-default: the only landing URL whose language nobody asked for. A visitor
// whose browser prefers one of the other four is sent to that language's own indexable URL
// instead of being served English — the URL still owns the language, so canonical, hreflang
// and the prerendered HTML stay in agreement. Only `/` redirects: /it, /es, /fr, /de were
// chosen (by a link, a switcher or a search result) and must render what they say they render.
//
// An explicit choice outranks the browser — someone who switched to English on a German
// machine stays on `/` — which is also why the redirect is not a one-shot: `<Landing>` saves
// the language of the URL it lands on, so the second visit resolves through the stored
// preference to the same place. Crawlers ask with an English Accept-Language and stay on `/`.
function RootLanding() {
  const location = useLocation();
  const preferred = storedLang() || browserLang() || DEFAULT_LANG;
  const target = pathForLang(preferred);
  if (target !== pathForLang(DEFAULT_LANG)) {
    return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
  }
  return <Landing lang={DEFAULT_LANG} />;
}

function RootRouter() {
  const location = useLocation();
  // Callback Google (session_id nel fragment). Limitato al percorso di redirect: prima
  // scattava su qualunque URL del sito — Netlify serve il bundle ovunque — quindi un link
  // con `#session_id=` verso /admin o /reset-password avviava comunque lo scambio.
  if (location.pathname === "/app" && location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* One indexable URL per language. English is the root (x-default), so `/en`
          only exists as a courtesy redirect — Netlify serves a real 301 for it. */}
      {SEO_LANGS.map((code) => (
        <Route key={code} path={pathForLang(code)}
          element={code === DEFAULT_LANG ? <RootLanding /> : <Landing lang={code} />} />
      ))}
      {/* The policy is a second prerendered cluster hanging off each landing URL. It is
          reachable from the app too (the footer link follows the UI language), which is why
          it lives outside the Protected wrapper. */}
      {SEO_LANGS.map((code) => (
        <Route key={`privacy-${code}`} path={privacyPathForLang(code)} element={<PrivacyPolicy lang={code} />} />
      ))}
      <Route path="/en" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/app" element={<Protected><ScreeningApp /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<Navigate to="/app" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <RootRouter />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
